import { EditOutlined, LinkOutlined } from "@ant-design/icons";
import { ICellRendererParams } from "ag-grid-enterprise";
import { Popover, Tag, Tooltip } from "antd";
import {
  MaterialLinksCellRenderer,
  TruncatedTextWithTooltip
} from "components/cell-renders";
import CustomTooltip from "components/custom-tooltip/custom-tooltip";
import FeatureLogSetStatusUI from "components/feature-status/feature-instance-status";
import {
  getUserFromSubmittalLogItem,
  transformDataFromSubmittalLogItem,
  updateSubmittalLogItemHistory
} from "components/microviz/microviz-data-transformations";
import MicrovizDateBlock from "components/microviz/microviz-dateblock";
import InfoIcon from "components/svg-icons/info-icon";
import UserTag from "components/user-tag/user-tag";
import TitleDescriptionTooltip from "components/widgets/title-description-tooltip";
import {
  AcceptanceStatus,
  ESubmittalStatus,
  SubmittalStatusToStrMap
} from "constants/index";
import { FeatureTypes } from "entity-app/constants";
import { getValueFromDotPath } from "entity-app/utils/utils";
import { Link } from "react-router-dom";
import ErrorBoundary from "components/error-boundary";
import SubmittalMicroVisualiser from "components/submittal-details/submittal-micro-viz";
import VoidSubmittalIcon from "components/svg-icons/void-submittal-icon";
import SubmittalMicroVizDateBlock from "components/submittal-details/submittal-microviz-dateblock";

export const linkCellRenderer = ({
  value,
  data,
  projectId,
  featureKey
}: any) => {
  return (
    <Link
      className="gridIdLink"
      to={{
        pathname: `/project/${projectId}/${featureKey}/${
          data?.id || data?.feature_instance_id || ""
        }`
      }}
    >
      <TruncatedTextWithTooltip text={value} />
    </Link>
  );
};

export const userEntityCellRenderer = ({
  value,
  valueFormatted,
  statusId,
  companyObjField,
  data,
  context,
  node,
  column,
  colDef
}: any) => {
  const text = valueFormatted || value;
  if (typeof text !== "string") return "";
  const statusIdValue = getValueFromDotPath(data, statusId);
  const companyObj = getValueFromDotPath(data, companyObjField);
  const colId = colDef?.colId || colDef?.field;
  return (
    <div className="w-full flex items-center">
      <div className="overflow-hidden truncate grow flex-col leading-none space-y-1.5">
        <ErrorBoundary>
          <TruncatedTextWithTooltip
            text={text}
            tooltipContent={
              <TitleDescriptionTooltip
                title={value}
                description={companyObj?.name}
              />
            }
            dataTestId={colId}
          />
        </ErrorBoundary>

        {companyObj && (
          <div
            className="text-ciq-gray-500b"
            data-testid={`${colId}_company-name`}
          >
            {companyObj?.name}
          </div>
        )}

        {statusIdValue === AcceptanceStatus.DEACTIVATED && (
          <div
            className="absolute flex w-full items-center justify-end pr-4 -top-1 pointer-events-none"
            data-testid="inactive-user-tag"
          >
            <UserTag label="Inactive" />
          </div>
        )}
      </div>
      <div>
        <EditOutlined
          className="cell-edit-icon"
          onClick={() => {
            if (context.onEditCell)
              context.onEditCell({
                rowIndex: node.rowIndex!,
                colKey: column!.getId(),
                data
              });
          }}
        />
      </div>
    </div>
  );
};

export const ArrayStringComparator = (a: any, b: any) => {
  return (a?.[0]?.name || "").localeCompare(b?.[0]?.name || "");
};

export const ArrayStringGetQuickFilterText = ({ value }: any) => {
  if (value && Array.isArray(value)) {
    return value.map((v) => v.name).join(" ");
  }
  return "";
};

export function ArrayStringCellRenderer(params: any) {
  const { value, featureKey, context, column, node, data } = params;

  const arr = (value || []) as Array<{ id: string; name: string }>;

  const toShow = arr.slice(0, 2);
  const remaining = arr.slice(2, arr.length);

  const popupContent = (
    <div className="max-w-[260px] m-0 text-xs flex flex-wrap gap-y-1 gap-x-0">
      {remaining.map((item: any) => {
        return (
          <Link
            key={`${item.id}${item.name}`}
            target="_blank"
            to={`${featureKey}/${item.id}`}
            onClick={(evt) => (featureKey ? null : evt.preventDefault())}
          >
            <Tag
              color="default"
              icon={<LinkOutlined />}
              className="bg-gray-100 w-[120px] truncate"
            >
              <CustomTooltip value={item.name} />
            </Tag>
          </Link>
        );
      })}
    </div>
  );

  return (
    <div className="w-full flex items-center">
      <div className="grow overflow-hidden truncate flex items-center py-1 -pr-4 gap-y-1 gap-x-0">
        {toShow.map((item, index: number) => {
          return (
            <div className="flex items-center" key={`${item.id}${item.name}`}>
              <Link
                target="_blank"
                to={`${featureKey}/${item.id}`}
                onClick={(evt) => (featureKey ? null : evt.preventDefault())}
                className="leading-normal"
              >
                <Tag
                  color="default"
                  icon={<LinkOutlined />}
                  className="bg-gray-100 w-[120px] truncate"
                >
                  <CustomTooltip value={item.name} />
                </Tag>
              </Link>
              {index === 1 && arr.length > 2 ? (
                <div className="flex-none flex-grow-0 leading-none -m-1">
                  <Popover content={popupContent}>
                    <div>+{remaining.length}</div>
                  </Popover>
                </div>
              ) : (
                ""
              )}
            </div>
          );
        })}
      </div>
      <EditOutlined
        className="cell-edit-icon px-1"
        onClick={() => {
          if (context.onEditCell)
            context.onEditCell({
              rowIndex: node.rowIndex!,
              colKey: column!.getId(),
              data
            });
        }}
      />
    </div>
  );
}

export const twoRowGetQuickFilterText = ({ data }: any) => {
  try {
    return `${data.title}\n${data.description}`;
  } catch (error) {
    return "";
  }
};

export const twoRowCellRenderer = (props: any) => {
  const { data, context, featureKey, projectId, colDef } = props;

  if (!data) return null;

  const colId = colDef?.colId || colDef?.field;

  return (
    <div className="w-full">
      <div className="pl-3 !flex !items-center space-x-1">
        <Link
          className="gridIdLink grow truncate text-left"
          to={{
            pathname: `/project/${projectId}/${featureKey}/${data.id}`
          }}
        >
          <TruncatedTextWithTooltip text={data.title} dataTestId={colId} />
        </Link>
        <EditOutlined
          className="cell-edit-icon px-2"
          onClick={() => {
            context.onEditCell({
              rowIndex: props.node.rowIndex!,
              colKey: props.column!.getId(),
              data
            });
          }}
        />
      </div>
      <div className="colSecondValue pl-3">
        <TruncatedTextWithTooltip text={data.description} />
      </div>
    </div>
  );
};

export const materialEarlyReleaseCellRenderer = ({ data }: any) => {
  const reason =
    data?.wf_override_reason || data?.material_released_early || "";
  if (reason) {
    return (
      <div className="flex items-center space-x-2">
        <span>Yes</span>
        <Tooltip title={`Reason cited: ${reason}`}>
          <span className="flex items-center">
            <InfoIcon />
          </span>
        </Tooltip>
      </div>
    );
  }
  return "";
};

export const featureStatusCellRenderer = (params: any) => {
  const { data, value, context } = params;

  const isEditable = context?.editPermission;

  const userDetails = (
    <div className="flex space-x-2 items-center justify-between">
      <div className="flex truncate">
        <TruncatedTextWithTooltip text={value} />
      </div>

      {isEditable && (
        <FeatureLogSetStatusUI
          data={data}
          refetchLogData={context?.refetchLogData}
        />
      )}
    </div>
  );

  return userDetails;
};

export const submittalStatusCellRendererV2Report = (params: any) => {
  const { data, context } = params;
  const { isIntegrationMode } = context;

  if (data.workflow_status === ESubmittalStatus.VOID) {
    return <VoidSubmittalIcon />;
  }

  let dataForMicroviz: any = null;
  if (!isIntegrationMode) {
    const updatedHistory = updateSubmittalLogItemHistory(
      data,
      context.projectParticipants
    );

    dataForMicroviz = {
      submittal_history: updatedHistory,
      design_reviewer_id: data?.design_reviewer_id,
      gc_reviewer_id: data?.gc_reviewer_id,
      vendorInfo: {
        designReviewerCompanyName: null,
        responsibleContractorCompanyName: null
      }
    };
  }

  return (
    <div className="flex flex-col justify-start leading-none">
      <div className="leading-normal">
        {SubmittalStatusToStrMap[data.workflow_status]}
      </div>
      {isIntegrationMode && (
        <div className="mt-1.5">
          <ErrorBoundary>
            <SubmittalMicroVizDateBlock
              size="sm"
              submittalData={data}
              projectParticipants={context.projectParticipants}
              submittalRevisions={data.revision_date_blocks}
              selectedRevision={data.revision}
            />
          </ErrorBoundary>
        </div>
      )}

      {!isIntegrationMode && (
        <div className="mt-1.5">
          <ErrorBoundary>
            <SubmittalMicroVisualiser {...dataForMicroviz} size="sm" />
          </ErrorBoundary>
        </div>
      )}
    </div>
  );
};

export const submittalStatusCellRendererV2 = (params: any) => {
  const { data, context } = params;
  const { isIntegrationMode } = context;

  if (data.workflow_status.id === ESubmittalStatus.VOID) {
    return <VoidSubmittalIcon />;
  }

  let dataForMicroviz: any = null;
  if (!isIntegrationMode) {
    const updatedHistory = updateSubmittalLogItemHistory(
      data,
      context.projectParticipants
    );

    dataForMicroviz = {
      submittal_history: updatedHistory,
      design_reviewer_id: data?.design_reviewer?.id,
      gc_reviewer_id: data?.gc_user?.id,
      vendorInfo: {
        designReviewerCompanyName: data?.design_reviewer?.company?.name,
        responsibleContractorCompanyName: data?.responsible_contractor?.name
      }
    };
  }
  return (
    <div
      className="flex flex-col justify-start leading-none"
      data-testid="submittal-status-cell-renderer"
    >
      <div className="leading-normal">
        {SubmittalStatusToStrMap[data.workflow_status.id]}
      </div>
      {isIntegrationMode && (
        <div className="mt-1.5">
          <ErrorBoundary>
            <MicrovizDateBlock
              submittalData={{ ...data, revision: 0, revision_date_blocks: [] }}
              submittalRevisions={[]}
              selectedRevision={0}
              dataTransformFunction={transformDataFromSubmittalLogItem}
              userGetterFunction={getUserFromSubmittalLogItem}
              size="sm"
            />
          </ErrorBoundary>
        </div>
      )}

      {!isIntegrationMode && (
        <div className="mt-1.5">
          <ErrorBoundary>
            <SubmittalMicroVisualiser {...dataForMicroviz} size="sm" />
          </ErrorBoundary>
        </div>
      )}
    </div>
  );
};

export function FeatureMaterialLinksCellRenderer(params: ICellRendererParams) {
  const { data, colDef } = params;
  const colId = colDef?.colId || colDef?.field;

  const rendererParams = {
    ...params,
    data: {
      materials: data?.linked_items
        .filter(
          (item: any) =>
            item.feature_type === FeatureTypes.MATERIALS && !item.implicit
        )
        .map((item: any) => ({
          ...item,
          material_sequence_id: item.sequence_id,

          spec_section_no: item.spec_section?.length
            ? item.spec_section[0].number
            : "",
          spec_section_name: item.spec_section?.length
            ? item.spec_section[0].name
            : ""
        }))
    }
  };

  return (
    <div className="leading-none" data-testid={`${colId}`}>
      {MaterialLinksCellRenderer(rendererParams)}
    </div>
  );
}
